import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  UserIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  AcademicCapIcon,
  ShoppingBagIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import { useThemeColors } from '../../hooks/useThemeColors'
import { triggerHaptic } from '../../hooks/useMobile'
import MobileBottomSheet from '../mobile/MobileBottomSheet'
import '../mobile/MobileMenuSheet.css'

interface MenuItem {
  path: string
  label: string
  icon: React.ElementType
  description?: string
}

const managementItems: MenuItem[] = [
  {
    path: '/bookings',
    label: 'Bookings',
    icon: CreditCardIcon,
    description: 'View all bookings'
  },
  {
    path: '/classes-camps',
    label: 'Classes & Camps',
    icon: AcademicCapIcon,
    description: 'Manage class types'
  },
  {
    path: '/packages',
    label: 'Packages',
    icon: CreditCardIcon,
    description: 'Session packages'
  },
  {
    path: '/facilities',
    label: 'Facilities',
    icon: BuildingOffice2Icon,
    description: 'Manage locations'
  },
  {
    path: '/staff',
    label: 'Staff',
    icon: UserGroupIcon,
    description: 'Manage team'
  },
]

const businessItems: MenuItem[] = [
  {
    path: '/billing',
    label: 'Billing',
    icon: CurrencyDollarIcon,
    description: 'Invoices & payments'
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: ChartBarIcon,
    description: 'Analytics & insights'
  },
  {
    path: '/store',
    label: 'Store',
    icon: ShoppingBagIcon,
    description: 'Manage merchandise'
  },
  {
    path: '/email-templates',
    label: 'Email Templates',
    icon: EnvelopeIcon,
    description: 'Customize emails'
  },
]

const settingsItems: MenuItem[] = [
  {
    path: '/settings',
    label: 'Settings',
    icon: Cog6ToothIcon,
    description: 'App settings'
  },
  {
    path: '/organization',
    label: 'Organization',
    icon: BuildingOffice2Icon,
    description: 'Business settings'
  },
]

interface AdminMobileMenuSheetProps {
  isOpen: boolean
  onClose: () => void
}

export default function AdminMobileMenuSheet({ isOpen, onClose }: AdminMobileMenuSheetProps) {
  const { user, profile, signOut } = useAuth()
  const navigate = useNavigate()
  const { colors } = useThemeColors()

  const handleNavigation = (path: string) => {
    triggerHaptic('light')
    onClose()
    navigate(path)
  }

  const handleSignOut = async () => {
    triggerHaptic('medium')
    onClose()
    await signOut()
    navigate('/login')
  }

  return (
    <MobileBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Menu"
      height="full"
      className="mobile-menu-sheet"
    >
      {/* User Info Card */}
      <div className="mobile-menu-user-card" style={{ '--primary-color': colors.primary } as React.CSSProperties}>
        <div className="mobile-menu-avatar" style={{ background: colors.primary }}>
          {profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'A'}
        </div>
        <div className="mobile-menu-user-info">
          <span className="mobile-menu-user-name">
            {profile?.full_name || user?.email?.split('@')[0] || 'Admin'}
          </span>
          <span className="mobile-menu-user-email">{user?.email}</span>
        </div>
      </div>

      {/* Management Items */}
      <div className="mobile-menu-section">
        <h3 className="mobile-menu-section-title">Management</h3>
        <div className="mobile-menu-items">
          {managementItems.map((item) => (
            <button
              key={item.path}
              className="mobile-menu-item"
              onClick={() => handleNavigation(item.path)}
            >
              <div className="mobile-menu-item-icon">
                <item.icon />
              </div>
              <div className="mobile-menu-item-content">
                <span className="mobile-menu-item-label">{item.label}</span>
                {item.description && (
                  <span className="mobile-menu-item-desc">{item.description}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Business Items */}
      <div className="mobile-menu-section">
        <h3 className="mobile-menu-section-title">Business</h3>
        <div className="mobile-menu-items">
          {businessItems.map((item) => (
            <button
              key={item.path}
              className="mobile-menu-item"
              onClick={() => handleNavigation(item.path)}
            >
              <div className="mobile-menu-item-icon">
                <item.icon />
              </div>
              <div className="mobile-menu-item-content">
                <span className="mobile-menu-item-label">{item.label}</span>
                {item.description && (
                  <span className="mobile-menu-item-desc">{item.description}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Settings Section */}
      <div className="mobile-menu-section">
        <h3 className="mobile-menu-section-title">Settings</h3>
        <div className="mobile-menu-items">
          {settingsItems.map((item) => (
            <button
              key={item.path}
              className="mobile-menu-item"
              onClick={() => handleNavigation(item.path)}
            >
              <div className="mobile-menu-item-icon">
                <item.icon />
              </div>
              <div className="mobile-menu-item-content">
                <span className="mobile-menu-item-label">{item.label}</span>
                {item.description && (
                  <span className="mobile-menu-item-desc">{item.description}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sign Out */}
      <div className="mobile-menu-footer">
        <button
          className="mobile-menu-signout"
          onClick={handleSignOut}
        >
          <ArrowRightOnRectangleIcon />
          <span>Sign Out</span>
        </button>
      </div>
    </MobileBottomSheet>
  )
}
